if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
      };
      this.mql = window.matchMedia('(min-width: 750px)');
      this.request = 0;
      this.elements.viewer?.addEventListener('slideChanged', (event) => this.onSlideChanged(event));
      // Delegation also covers thumbnails replaced by native variant rendering.
      this.elements.thumbnails?.addEventListener('click', (event) => {
        const button = event.target.closest('button');
        const thumbnail = button?.closest('[data-target]');
        if (thumbnail) this.setActiveMedia(thumbnail.dataset.target, false);
      });
      if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
    }

    connectedCallback() {
      this.preloadNeighbors(this.elements.viewer?.querySelector('.is-active'));
    }

    disconnectedCallback() {
      this.cancelPendingMedia();
      clearTimeout(this.announceTimer);
    }

    cancelPendingMedia() { this.request += 1; }

    reportImageError(failed) {
      const message = this.querySelector('[data-gallery-error]');
      if (message) message.hidden = !failed;
    }

    preloadNeighbors(active) {
      [active?.previousElementSibling, active?.nextElementSibling].forEach((slide) => {
        const image = slide?.querySelector('.product__media img');
        if (image) { image.loading = 'eager'; image.decode().catch(() => {}); }
      });
    }

    onSlideChanged(event) {
      const active = event.detail?.currentElement;
      if (!active) return;
      const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${active.dataset.mediaId}"]`);
      this.setActiveThumbnail(thumbnail);
      this.preloadNeighbors(active);
    }

    syncThumbnails(source) {
      const sourceThumbnails = source?.querySelector('[id^="GalleryThumbnails"]');
      const thumbnails = this.elements.thumbnails;
      if (!sourceThumbnails || !thumbnails) return;
      const sourceList = sourceThumbnails.querySelector('ul');
      const list = thumbnails.querySelector('ul');
      if (sourceList && list) list.innerHTML = sourceList.innerHTML;
      thumbnails.className = sourceThumbnails.className;
      ['previous', 'next'].forEach((name) => {
        const button = thumbnails.querySelector(`button[name="${name}"]`);
        const sourceButton = sourceThumbnails.querySelector(`button[name="${name}"]`);
        if (button && sourceButton) button.className = sourceButton.className;
      });
      thumbnails.resetPages?.();
    }

    async setActiveMedia(mediaId, prepend) {
      const request = ++this.request;
      const active = this.elements.viewer?.querySelector(`[data-media-id="${mediaId}"]`);
      if (!active) return;
      const image = active.querySelector('.product__media img');
      try {
        if (image) {
          image.loading = 'eager';
          if (!image.complete || !image.naturalWidth) await image.decode();
        }
      } catch (error) {
        if (request === this.request && this.isConnected) this.reportImageError(true);
        return;
      }
      if (request !== this.request || !this.isConnected) return;
      this.reportImageError(false);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((item) => item.classList.remove('is-active'));
      active.classList.add('is-active');
      const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
      if (prepend) {
        if (active.parentElement.firstElementChild !== active) active.parentElement.prepend(active);
        if (thumbnail && thumbnail.parentElement.firstElementChild !== thumbnail) thumbnail.parentElement.prepend(thumbnail);
        this.elements.viewer.resetPages?.();
        this.elements.thumbnails?.resetPages?.();
      }
      this.preventStickyHeader();
      if (!this.mql.matches || this.elements.thumbnails) active.parentElement.scrollTo({ left: active.offsetLeft });
      const bounds = active.getBoundingClientRect();
      if (bounds.top < -0.5) {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: bounds.top + window.scrollY, behavior: reduced ? 'auto' : 'smooth' });
      }
      this.playActiveMedia(active);
      this.setActiveThumbnail(thumbnail);
      this.announceLiveRegion(thumbnail?.dataset.mediaPosition);
      this.preloadNeighbors(active);
    }

    setActiveThumbnail(thumbnail) {
      if (!thumbnail || !this.elements.thumbnails) return;
      this.elements.thumbnails.querySelectorAll('button').forEach((button) => button.removeAttribute('aria-current'));
      thumbnail.querySelector('button')?.setAttribute('aria-current', 'true');
      if (!this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) {
        this.elements.thumbnails.slider?.scrollTo({ left: thumbnail.offsetLeft });
      }
    }

    announceLiveRegion(position) {
      if (!position || !this.elements.liveRegion) return;
      clearTimeout(this.announceTimer);
      this.elements.liveRegion.setAttribute('aria-hidden', 'false');
      this.elements.liveRegion.textContent = window.accessibilityStrings.imageAvailable.replace('[index]', position);
      this.announceTimer = setTimeout(() => this.elements.liveRegion.setAttribute('aria-hidden', 'true'), 2000);
    }

    playActiveMedia(active) {
      window.pauseAllMedia();
      active.querySelector('.deferred-media')?.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      this.stickyHeader?.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer?.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
    }
  });
}
